import React from 'react';
import { Text, Box, Divider, Select, Group } from '@cowbell/ui';
import moment from 'moment';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import CbButton from '../../../components/Buttons/CbButton';
import type { EnrichedCmailMessage } from '../../../api/cmail/cmail.dto';
import {
  normalizeMessageCategoryForAPI,
  normalizeMessageCategoryForUI,
} from '../utils/emailTypeUtils';
import { extractEmailAddress } from '../utils/emailAddressUtils';
import { reprocessSubCategories } from '../utils/tabSubCategories';
import classes from './CmailReprocessConfirmationModal.module.css';

interface CmailReprocessConfirmationModalProps {
  data: {
    message: EnrichedCmailMessage;
    onConfirm: (category: string, subCategory?: string) => Promise<void>;
    isReprocessing: boolean;
  };
  close: () => void;
}

// The listing puts msgCategory through normalizeMessageCategoryForUI before the modal ever sees
// it, so the default arrives in display form -- 'Claims', 'LossRuns', 'ClaimsOps', 'Renewal'. The
// options below are therefore spelled the same way; an option list in enum form leaves the Select
// with a value it cannot match, which renders blank and resolves no subcategory list.
//
// The services downstream match on the enum name instead (email_classifier's VALID_CATEGORIES
// holds CLAIMS_OPS, not ClaimsOps), so the selection is converted on the way out. The two
// normalizers are inverses of each other, which is what keeps this round-trip from drifting.
const toReprocessCategoryOption = normalizeMessageCategoryForUI;
const toReprocessCategory = normalizeMessageCategoryForAPI;

// Keyed by the option as displayed, to match the default the listing supplies.
//
// The subcategories come from the same per-tab list the inbox's filter chips use, so every chip
// can be chosen here. That list mirrors VALID_SUB_CATEGORIES in the extractor's
// email_classifier/config.py, where membership is an exact set match: a value outside that set
// resolves no schema and the reprocess extracts nothing, which is why legacy values are left out.
//
// For CLAIMS_OPS the subcategory is the workstream only. Extraction is gated on the act, its
// subSubCategory, which the modal cannot set, so the classifier always re-derives the act, under
// whatever workstream is sent -- or re-derives both when none is.
const REPROCESS_CATEGORIES = [
  'Submission',
  'LossRuns',
  'RiskEng',
  'Claims',
  'ClaimsOps',
  'Binders',
  'BOR',
  'CRS',
  'Subjectivity',
  'Spam',
  'MTE',
  'Renewal',
  'Cancellation',
  'CertRequest',
  'Compliance',
  'API',
  'General',
  'Finance',
  'Other',
];

const subCategoryOptionsMap: Record<string, string[]> = Object.fromEntries(
  REPROCESS_CATEGORIES.map((category) => [
    category,
    reprocessSubCategories(category),
  ])
);

/**
 * The category a submit actually sends.
 *
 * Never lets an empty selection reach normalizeMessageCategoryForAPI, which reads a missing value
 * as SUBMISSION -- so a cleared field would quietly reprocess a claims ops email as a submission.
 * Falls back to the classification the email already carries instead: reprocessing under the
 * category it was filed as is recoverable, silently reclassifying it is not.
 */
export const submittedCategory = (
  formCategory?: string,
  messageCategory?: string
): string =>
  toReprocessCategory(
    formCategory || toReprocessCategoryOption(messageCategory)
  );

export const REPROCESS_CATEGORY_OPTIONS = Object.keys(subCategoryOptionsMap);

// A subcategory only survives if the chosen category accepts it. Without this a
// stored value, or one left behind by switching category, rides along into the
// payload and lands outside the extractor's set.
//
// Both arguments arrive nullable -- the Select types its value `string | null`, and the form
// holds null until an operator picks a subcategory. Neither null needs a guard at the call
// site: an unknown key resolves no option list, and a missing subcategory is already falsy,
// so both fall through to '' on their own.
const keepSubCategory = (category: string, subCategory?: string | null) =>
  subCategory && subCategoryOptionsMap[category]?.includes(subCategory)
    ? subCategory
    : '';

export {
  subCategoryOptionsMap,
  toReprocessCategory,
  toReprocessCategoryOption,
};

export const CmailReprocessConfirmationModal = ({
  data,
  close,
}: CmailReprocessConfirmationModalProps) => {
  const { message, onConfirm, isReprocessing } = data;

  const defaultCategory = toReprocessCategoryOption(
    Array.isArray(message.msgCategory)
      ? message.msgCategory[0]
      : message.msgCategory
  );

  // The subcategory starts empty rather than on the stored one. The classifier skips its own
  // subcategory step whenever one is sent, so pre-filling it pinned every reprocess to the
  // classification being corrected: a claims ops invoice stored as 'other' came back as 'other',
  // with no act under it and no invoice extracted. Empty lets the classifier re-derive it, the
  // same as on first arrival.
  const methods = useForm<{ category: string; subCategory: string | null }>({
    defaultValues: {
      category: defaultCategory,
      subCategory: null,
    },
  });

  const { handleSubmit, control, watch, setValue } = methods;
  const selectedCategory = watch('category');
  const subCategoryOptions = subCategoryOptionsMap[selectedCategory] || [];

  const onSubmit = async (formData: {
    category: string;
    subCategory: string | null;
  }) => {
    await onConfirm(
      submittedCategory(formData.category, message.msgCategory),
      // Omitted rather than sent blank, so "reclassify it" has one shape on the wire.
      formData.subCategory || undefined
    );
    close();
  };

  const handleCancel = () => {
    close();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.modalBody}>
          <Text size="sm" mb="sm">
            Are you sure you want to rerun all data extraction for this email?
            This action will:
          </Text>
          <Box mb="sm">
            <Text size="xs" component="ul" className={classes.bulletList}>
              <li>Re-extract all data from the email content</li>
              <li>Re-analyze attachments and documents</li>
              <li>Update the extracted data with new results</li>
              <li>This action cannot be undone</li>
            </Text>
          </Box>
          <Divider my="sm" />
          <Box mb="sm">
            <Text size="xs" mb="xs">
              <strong>Subject:</strong> {message.subject}
            </Text>
            <Text size="xs" mb="xs">
              <strong>From:</strong> {message.senderName} (
              {extractEmailAddress(message.senderEmail)})
            </Text>
            <Text size="xs" mb="xs">
              <strong>Received:</strong>{' '}
              {moment(message.dateReceived).format('MMM D, YYYY [at] h:mm A')}
            </Text>
            {message.emailType && (
              <Text size="xs" mb="xs">
                <strong>Type:</strong> {message.emailType}
              </Text>
            )}
            {message.attachments && message.attachments.length > 0 && (
              <Text size="xs" mb="xs">
                <strong>Attachments:</strong> {message.attachments.length}{' '}
                file(s)
              </Text>
            )}
            <Box className={classes.categoryRow}>
              <Text size="xs" fw="bold" mr="xs">
                Category:
              </Text>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select
                    size="xs"
                    className={classes.categorySelect}
                    data={REPROCESS_CATEGORY_OPTIONS.map((option) => ({
                      label: option,
                      value: option,
                    }))}
                    value={field.value}
                    // Deselecting would hand submit an empty category, and there is no such thing
                    // as reprocessing under no category.
                    allowDeselect={false}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue(
                        'subCategory',
                        keepSubCategory(value, watch('subCategory')) || null
                      );
                    }}
                    comboboxProps={{ withinPortal: true, zIndex: 1400 }}
                  />
                )}
              />
              {subCategoryOptions.length > 0 && (
                <Controller
                  name="subCategory"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="xs"
                      className={classes.subCategorySelect}
                      placeholder="Reclassify"
                      // An operator who picks one and changes their mind has to be able to go
                      // back to letting the classifier decide.
                      clearable
                      data={subCategoryOptions.map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      // null, not '': Mantine only blanks the displayed label
                      // for null, so an emptied value would leave the previous
                      // subcategory on screen while submitting nothing.
                      value={field.value || null}
                      onChange={(value) => field.onChange(value)}
                      comboboxProps={{ withinPortal: true, zIndex: 1400 }}
                    />
                  )}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Group justify="flex-end" p="md">
          <CbButton
            onClick={handleCancel}
            styleName="cancel"
            disabled={isReprocessing}
            isShowing
          >
            Cancel
          </CbButton>
          <CbButton
            type="submit"
            styleName="ctaButton"
            disabled={isReprocessing}
            loading={isReprocessing}
            isShowing
          >
            {isReprocessing ? 'Reprocessing...' : 'Reprocess Email'}
          </CbButton>
        </Group>
      </form>
    </FormProvider>
  );
};

export const CmailReprocessConfirmationModalConfig = {
  CmailReprocessConfirmationModal: {
    component: CmailReprocessConfirmationModal,
    config: {
      fullWidth: true,
      maxWidth: 'sm',
      title: 'Confirm Email Reprocessing',
    },
  },
};
